import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  LinearProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import CustomMenuList from "../../customComponents/CustomMenuList";
import { useGetClassroomStudentsPaginatedQuery, useGetClassesBySectionPaginatedQuery } from "../classroom/classApiSlice";
import { useParams } from "react-router-dom";
import { useGetAssessmentsQuery } from "../settings/assessments/assessmentsApiSlice";
import ExamsScoreSheet from "../class-reportsheet/ExamsScoreSheet";
import { preloadClassroom } from "../../../app/preloadService";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";
import db from "../../../app/offlineDb";
import { selectCurrentUser } from "../auth/authSlice";
import { getOfflineContext } from "../../../app/offlineContext";

const PER_PAGE = 10;

function SectionReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedClassroom, setSelectedClassroom] = useState(0);
  const [activeStudent, setActiveStudent] = useState(0);
  const [classesPage, setClassesPage] = useState(0);
  const [studentsPage, setStudentsPage] = useState(0);
  const [openClasses, setOpenClasses] = useState(true);
  const [openStudents, setOpenStudents] = useState(false);
  const [warn, setWarn] = useState({ open: false, pending: null, type: null, cached: false });

  const { sectionId } = useParams();
  const { isOnline } = useNetworkStatus();
  const currentUser = useSelector(selectCurrentUser);

  const { data: classroomsData, isFetching: isLoadingClassrooms } =
    useGetClassesBySectionPaginatedQuery({
      section_id: sectionId,
      page: classesPage + 1,
      per_page: PER_PAGE,
    });

  const classrooms = classroomsData?.classrooms || [];
  const classroomsPagination = classroomsData?.pagination || { total_items: 0, page: 1, per_page: PER_PAGE, total_pages: 0 };

  const { data: studentsData, isFetching: isLoadingStudents } = useGetClassroomStudentsPaginatedQuery(
    classrooms?.[selectedClassroom]?.id
      ? {
          classroom_id: classrooms?.[selectedClassroom]?.id,
          page: studentsPage + 1,
          per_page: PER_PAGE,
        }
      : undefined,
    { skip: !classrooms?.[selectedClassroom]?.id }
  );

  const students = studentsData?.students || [];
  const studentsPagination = studentsData?.pagination || { total_items: 0, page: 1, per_page: PER_PAGE, total_pages: 0 };

  const { data: allAssessments, isFetching: isLoadingAssessments } =
    useGetAssessmentsQuery("all");

  const subSectionAssessments = allAssessments?.filter(
    (assessment) =>
      assessment.subsection_id ===
      classrooms?.[selectedClassroom]?.subsection_id
  );
  useEffect(() => {
    if (!isOnline || !classrooms?.[selectedClassroom]?.id) return;
    preloadClassroom(classrooms?.[selectedClassroom]?.id, [], { auth: { user: currentUser } });
  }, [classrooms, currentUser, isOnline, selectedClassroom]);

  const handleClassroomChange = async (idx) => {
    if (!isOnline) {
      const targetClassroomId = classrooms?.[idx]?.id;
      const ctx = getOfflineContext({ auth: { user: currentUser } });
      const cached = targetClassroomId && ctx.schoolId && ctx.userId && ctx.termId
        ? await db.students.get([ctx.schoolId, ctx.userId, ctx.termId, targetClassroomId]).catch(() => null)
        : null;
      setWarn({ open: true, pending: idx, type: "classroom", cached: !!cached });
      return;
    }
    setSelectedClassroom(idx);
    setActiveStudent(0);
    setStudentsPage(0);
    if (isMobile) {
      setOpenClasses(false);
      setOpenStudents(true);
    }
  };

  const handleStudentChange = async (idx) => {
    if (!isOnline) {
      const currentClassroomId = classrooms?.[selectedClassroom]?.id;
      const ctx = getOfflineContext({ auth: { user: currentUser } });
      const cached = currentClassroomId && ctx.schoolId && ctx.userId && ctx.termId
        ? await db.students.get([ctx.schoolId, ctx.userId, ctx.termId, currentClassroomId]).catch(() => null)
        : null;
      setWarn({ open: true, pending: idx, type: "student", cached: !!cached });
      return;
    }
    setActiveStudent(idx);
    if (isMobile) {
      setOpenStudents(false);
    }
  };

  const confirmSwitch = () => {
    if (warn.type === "classroom") {
      setSelectedClassroom(warn.pending);
      setActiveStudent(0);
      setStudentsPage(0);
      if (isMobile) {
        setOpenClasses(false);
        setOpenStudents(true);
      }
    } else {
      setActiveStudent(warn.pending);
      if (isMobile) {
        setOpenStudents(false);
      }
    }
    setWarn({ open: false, pending: null, type: null });
  };

  const cancelSwitch = () => setWarn({ open: false, pending: null, type: null });

  return (
    <Grid container columnSpacing={3}>
      <Dialog open={warn.open} onClose={cancelSwitch} maxWidth="xs" fullWidth>
        <DialogTitle>{warn.cached ? "You're offline" : "Data not loaded"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {warn.cached
              ? `You're offline. Data for this ${warn.type === "classroom" ? "class" : "student"} was loaded before you went offline — switching is safe. Changes will sync when you reconnect.`
              : `You're offline and the data for this ${warn.type === "classroom" ? "class" : "student"} hasn't been loaded onto this device. If you enter comments now, they may overwrite existing comments on the server.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {!warn.cached && <Button onClick={cancelSwitch} autoFocus>Stay here</Button>}
          <Button onClick={confirmSwitch} color={warn.cached ? "primary" : "warning"} variant="contained">
            {warn.cached ? "Continue" : "Switch anyway"}
          </Button>
          {warn.cached && <Button onClick={cancelSwitch}>Cancel</Button>}
        </DialogActions>
      </Dialog>

      <Grid item xs={12} md={3} lg={2}>
        {isMobile ? (
          <Accordion
            expanded={openClasses}
            onChange={(_, expanded) => setOpenClasses(expanded)}
            disableGutters
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Classes</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isLoadingClassrooms ? (
                <LinearProgress />
              ) : (
                <CustomMenuList
                  activeTab={selectedClassroom}
                  setActiveTab={handleClassroomChange}
                  heading="Classes"
                  list={classrooms?.map((classs) => classs.name)}
                />
              )}
              <TablePagination
                component="div"
                count={classroomsPagination?.total_items ?? 0}
                page={classesPage}
                rowsPerPage={PER_PAGE}
                rowsPerPageOptions={[PER_PAGE]}
                onPageChange={(_, newPage) => setClassesPage(newPage)}
                sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          <>
            {isLoadingClassrooms ? (
              <LinearProgress />
            ) : (
              <CustomMenuList
                activeTab={selectedClassroom}
                setActiveTab={handleClassroomChange}
                heading="Classes"
                list={classrooms?.map((classs) => classs.name)}
              />
            )}
            <TablePagination
              component="div"
              count={classroomsPagination?.total_items ?? 0}
              page={classesPage}
              rowsPerPage={PER_PAGE}
              rowsPerPageOptions={[PER_PAGE]}
              onPageChange={(_, newPage) => setClassesPage(newPage)}
              sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
            />
          </>
        )}
      </Grid>

      <Grid item xs={12} md={3} lg={3}>
        {isMobile ? (
          <Accordion
            expanded={openStudents}
            onChange={(_, expanded) => setOpenStudents(expanded)}
            disableGutters
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{classrooms?.[selectedClassroom]?.name ? `${classrooms?.[selectedClassroom]?.name} students` : "Students"}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isLoadingStudents ? (
                <LinearProgress />
              ) : (
                <CustomMenuList
                  activeTab={activeStudent}
                  setActiveTab={handleStudentChange}
                  heading={classrooms?.[selectedClassroom]?.name + " students"}
                  list={students?.map(
                    (student) => `${student?.first_name} ${student?.last_name}`
                  )}
                />
              )}
              <TablePagination
                component="div"
                count={studentsPagination?.total_items ?? 0}
                page={studentsPage}
                rowsPerPage={PER_PAGE}
                rowsPerPageOptions={[PER_PAGE]}
                onPageChange={(_, newPage) => setStudentsPage(newPage)}
                sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          <>
            {isLoadingStudents ? (
              <LinearProgress />
            ) : (
              <CustomMenuList
                activeTab={activeStudent}
                setActiveTab={handleStudentChange}
                heading={classrooms?.[selectedClassroom]?.name + " students"}
                list={students?.map(
                  (student) => `${student?.first_name} ${student?.last_name}`
                )}
              />
            )}
            <TablePagination
              component="div"
              count={studentsPagination?.total_items ?? 0}
              page={studentsPage}
              rowsPerPage={PER_PAGE}
              rowsPerPageOptions={[PER_PAGE]}
              onPageChange={(_, newPage) => setStudentsPage(newPage)}
              sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
            />
          </>
        )}
      </Grid>

      <Grid item xs={12} md={6} lg={7}>
        {isLoadingAssessments || isLoadingClassrooms || isLoadingStudents ? (
          <LinearProgress />
        ) : (
          <ExamsScoreSheet
            classId={classrooms?.[selectedClassroom]?.id || ""}
            student={students?.[activeStudent] || null}
            assessments={subSectionAssessments || []}
            isSectionHead={true}
            sectionId={sectionId}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default SectionReport;
